import { useState, useEffect, useRef } from "react";
import Otp from "components/atoms/Otp/Otp";
import Timer from "components/atoms/Timer/Timer";
import Button from "components/Form/Button";
import { useUserContext } from "context/UserContext";
import { useLang } from "hooks/useLang";
import { requestOtp, MessageType, OtpType } from "utils/helpers/requestOtp";
import InputNew from "components/Form/newAuthInput";
import { useMutation } from "@tanstack/react-query";
import { verifyOtp } from "api/user/verify";

interface VerifyOtpData {
  otp: string;
  email?: string;
  phone?: string;
  callingCode?: string;
}

interface AuthModalVerificationProps {
  authData: {
    inputValue: string;
    inputType: OtpType;
    callingCode: string;
    email?: string;
  };
  onVerificationSuccess: () => void;
  authMode: "login" | "signup";
  actionButtonsContainerClassName?: string;
  isInsideModal?: boolean;
}

const AuthModalVerification = ({
  authData,
  onVerificationSuccess,
  authMode,
  actionButtonsContainerClassName,
  isInsideModal,
}: AuthModalVerificationProps) => {
  const { t } = useLang();
  const { login } = useUserContext();
  const { mutate: loginMutate, isPending: isLoginPending } = login;

  const { inputValue, inputType, callingCode } = authData;

  const [otp, setOtp] = useState(new Array(4).fill(""));
  const [password, setPassword] = useState("");
  const [showResend, setShowResend] = useState(false);
  const [verifyInputsFilled, setVerifyInputsFilled] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setEmailVerified, setPhoneVerified } = useUserContext();

  const [verificationType, setVerificationType] = useState<
    "password" | "email" | "phone"
  >(
    authMode === "login"
      ? inputType === "email" || inputType === "phone"
        ? inputType
        : "password"
      : inputType === "email" || inputType === "phone"
      ? inputType
      : "email"
  );

  const otpInputRef = useRef<HTMLInputElement | null>(null);
  const hasSentOtp = useRef(false);

  useEffect(() => {
    if (!hasSentOtp.current && verificationType !== "password") {
      sendOtp();
      hasSentOtp.current = true;
    }
  }, [verificationType]);

  useEffect(() => {
    setVerifyInputsFilled(otp.join("").length === 4);
  }, [otp]);

  useEffect(() => {
    setError(null);
  }, [verificationType]);

  const sendOtp = async () => {
    setShowResend(false);
    setOtp(new Array(4).fill(""));

    const value =
      verificationType === "email"
        ? authData.email
        : verificationType === "phone"
        ? inputValue
        : null;

    if (!value) {
      setError("Value is missing");
      return;
    }

    const response = await requestOtp({
      value,
      inputType: verificationType as OtpType,
      callingCode,
    });

    if (response.success) {
      setError(null);
    } else {
      if (response.messageType === MessageType.RATE_LIMIT_EXCEEDED) {
        setIsRateLimited(true);
      } else {
        setError(response.message || "something_went_wrong");
      }
    }
  };

  const { mutate: verifyOtpMutate, isPending: isVerifyOtpPending } =
    useMutation({
      mutationFn: verifyOtp,
      onSuccess: (data: any) => {
        if (data.status !== 400 && !data.error) {
          setError(null);
          if (verificationType === "email") {
            setEmailVerified();
          }
          if (verificationType === "phone") {
            setPhoneVerified();
          }
          onVerificationSuccess();
        } else {
          setError(data.message || "signup_error");
        }
      },
      onError: (error: any) => {
        if (error.response?.data?.message === "rate_limit_exceeded") {
          setError("rate_limit_exceeded");
        } else if (error.response?.data?.message === "invalidOTP") {
          setError("invalidOTP");
        } else {
          setError("something_went_wrong");
        }
      },
    });

  const handleVerify = async () => {
    if (authMode === "login") {
      await handleVerifyAndLogin();
    } else {
      if (!verifyInputsFilled) {
        setError("Please enter the OTP");
        return;
      }

      const otpString = otp.join("");
      let verificationData: VerifyOtpData | undefined;

      if (verificationType === "email") {
        verificationData = {
          email: authData.email,
          otp: otpString,
        };
      } else if (verificationType === "phone") {
        verificationData = {
          callingCode,
          phone: inputValue,
          otp: otpString,
        };
      } else {
        setError("Invalid verification type");
        return;
      }

      verifyOtpMutate(verificationData);
    }
  };

  const handleVerifyAndLogin = async () => {
    if (verificationType !== "password" && !verifyInputsFilled) {
      setError("Please enter the OTP");
      return;
    }

    const otpString = otp.join("");
    let loginData: any;

    if (verificationType === "password") {
      if (!password) {
        setError("Please enter your password");
        return;
      }
      loginData = { password };
    } else {
      loginData = { otp: otpString };
    }

    const inputData =
      inputType === "email"
        ? { email: inputValue, ...loginData }
        : { callingCode, phone: inputValue, ...loginData };

    loginMutate(inputData, {
      onSuccess: (data: any) => {
        if (
          data.status === 400 ||
          data.error ||
          (Array.isArray(data.errors) && data.errors.length > 0)
        ) {
          setError(data.message || "invalidLoginCredential");
        } else {
          setError(null);
          onVerificationSuccess();
        }
      },
      onError: (error: any) => {
        if (error.response?.data?.message === "rate_limit_exceeded") {
          setError("rate_limit_exceeded");
        } else if (error.response?.data?.message === "invalidOTP") {
          setError("invalidOTP");
        } else {
          setError("something_went_wrong");
        }
      },
    });
  };

  const showUserInfo = () => {
    const valueToShow =
      verificationType === "email" ? authData.email : inputValue;
    return (
      <div className="flex flex-col items-start">
        <p className="text-4.5  text-[#7B91EF] font-bold ">
          {t("the_code_has_been_sent_to")}{" "}
        </p>
        <p className="text-[24px] text-black dark:text-white font-bold overflow-hidden whitespace-nowrap text-ellipsis w-[300px] md:w-[430px]">
          {valueToShow}
        </p>
      </div>
    );
  };

  const isPending = authMode === "login" ? isLoginPending : isVerifyOtpPending;

  return (
    <div className="Form mx-4  flex flex-col items-start">
      <div className="pt-4  text-[1.3rem]">{showUserInfo()}</div>

      {verificationType === "password" ? (
        <div className="w-full pt-4 pb-6">
          <InputNew
            placeholder="************"
            label={t("password")}
            inputID={"password_login"}
            name={"password"}
            type={"password"}
            onChange={(e) => setPassword(e.target.value)}
            value={password}
            error={undefined}
            reqStar
          />
          {error && (
            <div className="my-5 text-center text-red-600">{t(error)}</div>
          )}
        </div>
      ) : (
        <div className="pt-6 text-center">
          <Otp
            otp={otp}
            setOtp={setOtp}
            isFilled={verifyInputsFilled}
            ref={otpInputRef}
            isError={!!error}
          />

          {error && (
            <div className="my-5 text-center text-red-600">{t(error)}</div>
          )}

          {isRateLimited ? (
            <div className="my-5 text-center text-red-600">
              {t("rate_limit_exceeded_try_again_later")}
            </div>
          ) : showResend ? (
            <div className="my-5 text-left">
              <div className="text-[14px]">
                {t("didnt_receive_code")}
                <span
                  onClick={() => sendOtp()}
                  className="mx-2 text-[#0DB9A0] text-[14px] hover:cursor-pointer hover:underline"
                >
                  {t("resend_code")}
                </span>
              </div>
            </div>
          ) : (
            <div className="my-5 ">
              <div className="text-sm">
                <Timer
                  initialMinute={1}
                  initialSeconds={30}
                  setShowResend={setShowResend}
                  hideText
                  color="text-black dark:text-white"
                />
              </div>
            </div>
          )}
        </div>
      )}

      <div className="w-full pb-10 flex flex-col">
        <div className="w-full bg-[#F0F0F033] h-[1px]"></div>
        {authMode === "login" ? (
          verificationType === "password" ? (
            <div
              onClick={() => {
                setVerificationType(inputType as "email" | "phone");
                hasSentOtp.current = false;
              }}
              className="flex cursor-pointer flex-row items-center mt-3"
            >
              <span className="text-[#0DB9A0] text-[14px] ml-2">
                {inputType === "email"
                  ? t("use_email_ver")
                  : t("use_phone_ver")}
              </span>
            </div>
          ) : (
            <div
              onClick={() => {
                setVerificationType("password");
                hasSentOtp.current = false;
              }}
              className="flex cursor-pointer flex-row items-center mt-3"
            >
              <span className="text-[#0DB9A0] text-[14px] ml-2">
                {t("usePassword")}
              </span>
            </div>
          )
        ) : verificationType === "phone" ? (
          <div
            onClick={() => {
              setVerificationType("email");
              hasSentOtp.current = false;
            }}
            className="flex cursor-pointer flex-row items-center mt-3"
          >
            <span className="text-[#0DB9A0] text-[14px] ml-2">
              {t("use_email_ver")}
            </span>
          </div>
        ) : (
          <div
            onClick={() => {
              setVerificationType("phone");
              hasSentOtp.current = false;
            }}
            className="flex cursor-pointer flex-row items-center mt-3"
          >
            <span className="text-[#0DB9A0] text-[14px] ml-2">
              {t("use_phone_ver")}
            </span>
          </div>
        )}
      </div>

      <div
        className={`mb-4 flex w-full flex-row items-center justify-between ${
          actionButtonsContainerClassName ? actionButtonsContainerClassName : ""
        }`}
      >
        <Button
          theme="modern"
          text={t("continue")}
          loading={isPending}
          onClick={handleVerify}
          className={`px-8 p-3 ${isInsideModal ? "mx-4" : "flex-[1]"}`}
        />
      </div>
    </div>
  );
};

export default AuthModalVerification;
